import SCORING from "../../data/spikingScoring.json";

// Drink/food-spiking reference data. Sources: NHS (Gloucestershire Hospitals PIL GHPI1714, Sheffield
// Teaching Hospitals PIL5250), GOV.UK spiking guidance, Rape Crisis England & Wales, Cleveland Clinic,
// PMC2750925 (detection windows). Educational only — not a diagnosis.
export const SOURCES = [
  { label: "GOV.UK — Spiking: advice and support", url: "https://www.gov.uk/guidance/spiking-advice-and-support" },
  { label: "NHS Gloucestershire — Spiking (patient leaflet)", url: "https://www.gloshospitals.nhs.uk/media/documents/Spiking_GHPI1714_01_25.pdf" },
  { label: "Cleveland Clinic — Symptoms of being roofied", url: "https://health.clevelandclinic.org/symptoms-of-being-roofied" },
  { label: "Rape Crisis England & Wales — What is spiking?", url: "https://rapecrisis.org.uk/get-informed/types-of-sexual-violence/what-is-spiking/" },
  { label: "PMC — Drug-facilitated sexual assault: detection windows", url: "https://pmc.ncbi.nlm.nih.gov/articles/PMC2750925/" },
  { label: "RAINN — Drug-facilitated sexual assault", url: "https://rainn.org/articles/drug-facilitated-sexual-assault" },
  { label: "SAMHSA — National Helpline & naloxone", url: "https://www.samhsa.gov/find-help/national-helpline" },
  { label: "Washington State Department of Health — Overdose prevention & naloxone", url: "https://doh.wa.gov/you-and-your-family/drug-user-health/overdose-education-naloxone-distribution" },
  { label: "Mayo Clinic — Escitalopram (Lexapro) and alcohol", url: "https://www.mayoclinic.org/drugs-supplements/escitalopram-oral-route/description/drg-20063707" },
];

// Symptom list + substance cards + weights all live in src/data/spikingScoring.json (editable without touching components).
export const SYMPTOMS = SCORING.symptoms;
export const SUBSTANCES = SCORING.substance_order.map((id) => ({ id, ...SCORING.substances[id] }));
export const EMERGENCY_SYMPTOMS = SCORING.emergency_symptoms;
export const EMERGENCY_TEXT = SCORING.emergency_text;
export const SHORT_WINDOW_NOTE = SCORING.short_window_note;

// Things that can look like spiking (or make a spiked drink hit harder). Not a reason to stay quiet — a reason to tell the clinician.
export const CAVEATS = [
  "SSRIs/SNRIs (Lexapro/escitalopram, Zoloft, Effexor…) can heighten your sensitivity to alcohol — one drink can feel like three. Tell staff what you take.",
  "Antihistamines, sleep aids and some painkillers add to alcohol's sedative effect.",
  "Low blood sugar, dehydration, heat and lack of sleep all amplify intoxication.",
  "Migraine, concussion, low blood pressure and panic attacks share symptoms with spiking — get checked either way.",
  "Several substances overlap. A symptom match narrows what to ask to be tested for; it cannot confirm anything. Only a lab test can.",
];

export const WHAT_TO_DO = [
  "Tell someone you trust your privacy with RIGHT NOW and stay with them. Do not leave alone or with someone you just met.",
  "Get to an Emergency Department — say clearly: \"I think my drink was spiked. Please take a urine sample now and send a comprehensive drug-facilitated assault toxicology panel, not the routine screen.\"",
  "Keep the drink if you can (don't pour it out). Note the time, place and what you drank.",
  "Testing windows are short: GHB can be gone from urine in about 12 hours. Hours matter.",
  "If you may have been assaulted, [any ER with a sexual assault nurse examiner](/safety/testing) can see you. The exam is free, and you do not have to report to police. You decide what happens next.",
];

// Emerging-threat alert (verified Oct 2026: KING 5, UNODC, Addiction Policy Forum, The Hill).
export const ALERT = {
  title: "New & deadly: Cychlorphine",
  body: "A lab-made synthetic opioid now in the U.S. street supply, including Washington — roughly **10× more potent than fentanyl**. It is pressed into counterfeit pills (fake Xanax, Percocet, Oxy) and cut into other drugs, so people rarely know they've taken it. **Fentanyl test strips and routine hospital drug screens do NOT detect it.** Overdoses can need multiple doses of naloxone. If someone is unresponsive or breathing slowly: call 911, give Narcan, keep giving it, stay until help arrives.",
  sources: [
    { label: "KING 5 — What is cychlorphine?", url: "https://www.king5.com/article/news/nation-world/what-is-cychlorphine-synthetic-opioid-linked-overdose-deaths/507-f1936135-8e5a-408e-bd0d-96f56a74aa5f" },
    { label: "Addiction Policy Forum — Public health alert", url: "https://www.addictionpolicy.org/post/public-health-alert-cychlorphine-a-highly-potent-emerging-synthetic-opioid" },
    { label: "UNODC — Cychlorphine", url: "https://www.unodc.org/LSS/Announcement/Details/9403c3d1-12b9-49ac-8604-7da583d38d3b" },
  ],
};

// Numbered bibliography shown in the North Star card; `refs` above index into this list (1-based).
export const BIBLIOGRAPHY = [...SOURCES, ...ALERT.sources];

// Retained from the original North Star Guide (/drink-spiking) — folded into the card as their own sections.
export const MISMATCH = {
  quote: "The worst hangovers aren't always hangovers.",
  intro: "This is where most of the truth surfaces: in a next-day conversation between friends.",
  quotes: ["I feel like I have the flu.", "Worst hangover of my life — and I only had two drinks.", "There's a chunk of the night I just don't have."],
  body: "One question cuts through: **how you feel versus what you actually drank.** When those two don't match, take it seriously — say so gently, without blame, and suggest medical care sooner rather than later. You may be the only person positioned to catch it.",
};

export const COUNT = {
  big: "1 in 10",
  body: "Drink spiking is engineered to be mistaken for something ordinary. The substances typically used clear the body within hours, so testing often comes back negative even when something happened — **a negative test cannot prove a clean night.** The symptoms read exactly like alcohol, a hangover or the flu, and most people who suspect something never report it. **Concealment isn't a side effect of this crime. It's the method.**",
  stat: "The best study we have (Swan et al., 2016, three US universities) found about 1 in 12 students reporting they'd been drugged — and that counts only the ones who suspected it.",
};

export const BEHAVIOR = {
  body: "The person who does this is protected by being liked. So **don't screen by reputation — screen by behavior:** insisting on getting or handling your drink; pushing you to drink faster; working to separate you from your friends; unusually attentive right as you start to fade; discouraging others from checking on you. None of these is proof. Together, they're a pattern — and a North Star watching behavior can't be charmed out of noticing.",
  close: "Every group needs a North Star. Be one.",
};

// Default order / state of the card's sections. Admins override this site-wide (site_config.north_star_guide).
export const DEFAULT_GUIDE_SECTIONS = [
  { key: "checker", title: "What should I focus my testing on?", enabled: true, collapsed: false, teaser: "" },
  { key: "what_to_do", title: "If something happens", enabled: true, collapsed: false, teaser: "" },
  { key: "alert", title: ALERT.title, enabled: true, collapsed: false, teaser: "" },
  { key: "mismatch", title: "The worst hangovers aren't always hangovers — the Mismatch Test", enabled: true, collapsed: false, teaser: "" },
  { key: "count", title: "1 in 10 is the cautious count", enabled: true, collapsed: false, teaser: "" },
  { key: "behavior", title: "Watch behavior, not reputation", enabled: true, collapsed: false, teaser: "" },
  { key: "caveats", title: "Before you panic — things that can look like spiking", enabled: true, collapsed: false, teaser: "" },
  { key: "sources", title: "Sources & bibliography", enabled: true, collapsed: false, teaser: "" },
];

// Merge a saved layout with the defaults (new sections appear at the end; unknown keys are dropped).
export const mergeGuideSections = (saved) => {
  const byKey = Object.fromEntries(DEFAULT_GUIDE_SECTIONS.map((s) => [s.key, s]));
  const out = (saved || []).filter((s) => byKey[s.key]).map((s) => ({ ...byKey[s.key], ...s, title: s.title || byKey[s.key].title }));
  const seen = new Set(out.map((s) => s.key));
  DEFAULT_GUIDE_SECTIONS.forEach((s) => { if (!seen.has(s.key)) out.push(s); });
  return out;
};

const strengthLabel = (score) => {
  const L = SCORING.labels;
  if (score >= L.strong.min) return { key: "strong", label: L.strong.label };
  if (score >= L.possible.min) return { key: "possible", label: L.possible.label };
  return { key: "less", label: L.less.label };
};

// Weighted score per substance: Σ(weight) × prior. Positive scores only, sorted desc; tie → higher prior, then list order.
export const scoreSubstances = (picked) => {
  const cols = SCORING._weights_columns;
  return SUBSTANCES.map((s, order) => {
    const col = cols.indexOf(s.id);
    const raw = picked.reduce((sum, sym) => sum + ((SCORING.weights[sym] || [])[col] || 0), 0);
    const score = Math.round(raw * s.prior * 10) / 10;
    return { ...s, raw, score, order, strength: strengthLabel(score) };
  })
    .filter((s) => s.score > 0)
    .sort((a, b) => b.score - a.score || b.prior - a.prior || a.order - b.order);
};

export const isEmergency = (picked) => picked.some((p) => EMERGENCY_SYMPTOMS.includes(p));

export const matchSubstances = scoreSubstances;
